<template>
  <div class="cy-plugin-widget-iframe">
    <CyEmptyData
      v-if="hasError"
      :title="$t('plugins.widgetUnavailableTitle')"
      :text="$t('plugins.widgetUnavailableText')">
      <template
        v-if="errorDetail"
        #actions>
        <p
          class="cy-plugin-widget-iframe__error-detail"
          role="alert"
          v-html="$sanitizeHtml(errorDetail)"/>
      </template>
    </CyEmptyData>
    <!-- Cross-origin widget content keeps the sandbox plus the origin/popup escapes it needs;
         same-origin content gets no sandbox, since allow-scripts + allow-same-origin would let it drop the sandbox anyway. -->
    <iframe
      v-else-if="showIframe"
      ref="iframe"
      title="Plugin Widget Iframe"
      :sandbox="iframeSandbox"
      :src="iframeUrl"
      :height="height"
      width="100%"
      @error="hasError = true"/>
    <div
      v-else
      class="cy-plugin-widget-iframe__placeholder"
      :style="{ height: `${height}px` }"/>
  </div>
</template>

<script>
import CyEmptyData from '@/components/CyEmptyData.vue'
import { useAuthStore } from '@/store/pinia/auth'

// setTimeout fires at once past this delay (about 24.8 days)
const MAX_TIMEOUT_MS = 2 ** 31 - 1

export default {
  name: 'CyPluginWidgetIframe',
  components: { CyEmptyData },
  props: {
    iframeContext: {
      type: Object,
      default: null,
    },
    height: {
      type: [String, Number],
      default: '100%',
    },
  },
  data: () => ({
    hasError: false,
    checked: false,
    errorDetail: null,
    // The iframe URL carries this widget ticket, never the session JWT (ENG-422)
    ticket: null,
    renewalTimer: null,
  }),
  computed: {
    showIframe () {
      return Boolean(this.iframeUrl) && this.checked
    },
    target () {
      if (!this.iframeContext) return null

      const {
        scope,
        orgCanonical,
        projectCanonical,
        envCanonical,
        componentCanonical,
        widgetId,
      } = this.iframeContext

      if (!orgCanonical || !widgetId) return null

      const isComponent = scope === 'component' && Boolean(projectCanonical && envCanonical && componentCanonical)
      return isComponent
        ? { isComponent, orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId }
        : { isComponent, orgCanonical, widgetId }
    },
    // A ticket opens one widget only: a new one is minted whenever the widget changes
    targetKey () {
      return this.target ? JSON.stringify(this.target) : null
    },
    iframeUrl () {
      if (!this.target || !this.ticket) return null

      const { isComponent, orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId } = this.target

      const endpoint = isComponent
        ? `/organizations/${orgCanonical}/projects/${projectCanonical}/environments/${envCanonical}/components/${componentCanonical}/plugin_widgets/${widgetId}/${this.ticket}/iframe/`
        : `/organizations/${orgCanonical}/plugin_widgets/${widgetId}/${this.ticket}/iframe/`

      // Appended, not resolved against the base: an absolute endpoint would drop the path
      // of a base such as https://host/api
      const baseUrl = import.meta.env.VUE_APP_API_URL
      const base = baseUrl.startsWith('http') ? baseUrl : window.location.origin + baseUrl
      const url = new URL(base.replace(/\/$/, '') + endpoint)

      const { path, routeSuffix, routeQuery, routeHash } = this.iframeContext

      if (path) {
        url.searchParams.append('path', path)
      }
      if (routeSuffix) {
        url.pathname = url.pathname.replace(/\/$/, '') + '/' + routeSuffix
      }
      if (routeQuery) {
        for (const [k, v] of Object.entries(routeQuery)) {
          url.searchParams.append(k, v)
        }
      }
      if (routeHash) {
        url.hash = routeHash
      }

      return url.toString()
    },
    iframeSandbox () {
      if (!this.iframeUrl) return null

      const isSameOrigin = new URL(this.iframeUrl).origin === window.location.origin

      return isSameOrigin
        ? null
        : 'allow-scripts allow-forms allow-popups allow-same-origin allow-popups-to-escape-sandbox'
    },
  },
  watch: {
    targetKey: {
      immediate: true,
      handler (key) {
        this.clearRenewal()
        this.ticket = null
        this.hasError = false
        this.checked = false
        this.errorDetail = null
        if (key) this.mintTicket()
      },
    },
    iframeUrl: {
      immediate: true,
      handler (url) {
        if (!url) return
        this.hasError = false
        this.checked = false
        this.errorDetail = null
        this.checkIframe()
      },
    },
  },
  mounted () {
    window.addEventListener('message', this.onIframeMessage)
  },
  beforeUnmount () {
    window.removeEventListener('message', this.onIframeMessage)
    this.clearRenewal()
  },
  methods: {
    onIframeMessage (event) {
      if (!this.iframeUrl) return
      // Another frame of the same origin, such as another plugin's widget, must not drive this one
      if (!event.source || event.source !== this.$refs.iframe?.contentWindow) return
      const iframeOrigin = new URL(this.iframeUrl).origin
      if (event.origin !== iframeOrigin) return

      const { type, path, query, hash } = event.data || {}
      if (type !== 'cycloid:navigate') return

      this.$router.replace({
        name: this.$route.name,
        params: { ...this.$route.params, pathSuffix: path || '' },
        ...(query && Object.keys(query).length ? { query } : {}),
        ...(hash ? { hash } : {}),
      }).catch(() => {})
    },
    async mintTicket () {
      const keyAtRequest = this.targetKey
      const { isComponent, orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId } = this.target
      const { ydAPI } = this.$cycloid

      const response = isComponent
        ? await ydAPI.createComponentPluginWidgetIframeTicket(orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId)
        : await ydAPI.createPluginWidgetIframeTicket(orgCanonical, widgetId)

      // Drop a stale ticket if the widget changed while it was minted
      if (keyAtRequest !== this.targetKey) return

      if (!response?.ok) {
        await this.showError(response)
        return
      }

      const { data } = await response.json()
      this.ticket = data.ticket
      this.scheduleRenewal(data.expires_at)
    },
    // The ticket lives as long as the session: renewing it reloads the iframe, so it is done
    // once it expires, and only while the user is still signed in
    scheduleRenewal (expiresAt) {
      this.clearRenewal()
      const delay = Math.min(Math.max(expiresAt * 1000 - Date.now(), 0), MAX_TIMEOUT_MS)
      this.renewalTimer = setTimeout(() => {
        this.renewalTimer = null
        if (useAuthStore().jwt && this.target) this.mintTicket()
      }, delay)
    },
    clearRenewal () {
      if (!this.renewalTimer) return
      clearTimeout(this.renewalTimer)
      this.renewalTimer = null
    },
    async showError (response) {
      try {
        const body = await response?.json()
        this.errorDetail = body?.errors?.[0]?.message || null
      } catch {
        this.errorDetail = null
      }
      this.hasError = true
      this.checked = true
    },
    async checkIframe () {
      const { isComponent, orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId } = this.target
      const urlAtRequest = this.iframeUrl
      const { ydAPI } = this.$cycloid

      const response = isComponent
        ? await ydAPI.getComponentPluginWidgetIframeProxy(orgCanonical, projectCanonical, envCanonical, componentCanonical, widgetId, this.ticket)
        : await ydAPI.getPluginWidgetIframeProxy(orgCanonical, widgetId, this.ticket)

      // Drop a stale preflight if the context changed while it was in flight.
      if (urlAtRequest !== this.iframeUrl) return

      if (!response?.ok) {
        await this.showError(response)
        return
      }

      this.hasError = false
      this.checked = true
    },
  },
}
</script>

<style lang="scss" scoped>
.cy-plugin-widget-iframe {
  width: 100%;
  height: 100%;

  iframe {
    border: none;
  }

  &__placeholder {
    width: 100%;
    background: cy-get-color('grey', 'light-2');
  }

  &__error-detail {
    margin-top: 8px;
    color: cy-get-color('grey', 'dark-1');
    font-size: 14px;
  }
}
</style>
